import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { ArrowDown, ArrowUp, Loader2, Quote } from "lucide-react";
import { api, apiErrorMessage } from "../../../lib/api";
import { shortDay } from "../../../lib/initialAssessment";

export const STATUS_WORDS = { pending: "În așteptare", published: "Publicat", unpublished: "Nepublicat", retracted: "Retras" };
const STATUS_TONE = {
  pending: "bg-[#FDE68A]/70 text-[#8a6a1a]", published: "bg-[#519872]/15 text-[#2F6B4E]",
  unpublished: "bg-[#EDEEF0] text-[#6B7280]", retracted: "bg-[#E63946]/10 text-[#b42a35]",
};
const FILTERS = ["", "pending", "published", "unpublished", "retracted"];

/**
 * "Testimoniale" (30.10.2026, T1–T6): the testimonials given with the final
 * feedback, consent included. Owner and admin FPED publish them, may shorten
 * one or fix a typo (never add; the original stays), order what the site
 * shows and take one off when its author withdraws consent.
 */
export default function TestimonialsCard({ canMutate }) {
  const [rows, setRows] = useState(null);
  const [filter, setFilter] = useState("");
  const [editing, setEditing] = useState(null); // {id, text}
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      setRows((await api.get("/admin/testimonials")).data.items || []);
    } catch {
      setRows([]);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const act = async (fn, ok) => {
    setBusy(true);
    try {
      await fn();
      if (ok) toast.success(ok);
      await load();
      return true;
    } catch (err) {
      toast.error(apiErrorMessage(err, "Nu am putut salva."));
      return false;
    } finally {
      setBusy(false);
    }
  };

  const status = (t, next, ok) => act(() => api.post(`/admin/testimonials/${t.id}/status`, { status: next }), ok);
  const retract = (t) => {
    if (window.confirm("Îl scoți de pe site? Folosește asta când cursantul își retrage acordul.")) {
      status(t, "retracted", "Testimonialul e retras.");
    }
  };
  const save = async () => {
    if (await act(() => api.put(`/admin/testimonials/${editing.id}/text`, { text: editing.text }), "Am salvat textul.")) {
      setEditing(null);
    }
  };
  const published = (rows || []).filter((t) => t.status === "published")
    .sort((a, b) => (a.order ?? 1e9) - (b.order ?? 1e9));
  const move = (t, step) => {
    const ids = published.map((x) => x.id);
    const i = ids.indexOf(t.id);
    const j = i + step;
    if (i < 0 || j < 0 || j >= ids.length) return;
    [ids[i], ids[j]] = [ids[j], ids[i]];
    act(() => api.put("/admin/testimonials/order", { ids }));
  };

  if (rows === null) return <p className="flex items-center gap-2 text-sm text-[#6B7280]"><Loader2 className="animate-spin" size={16} /> Se încarcă…</p>;
  const shown = filter === "published" ? published : rows.filter((t) => !filter || t.status === filter);

  return (
    <div className="rounded-2xl border border-[#E5E7EB] bg-white p-5" data-testid="testimonials-card">
      <h3 className="flex items-center gap-2 font-semibold text-[#373A45]"><Quote size={16} className="text-[#965EA7]" /> Testimoniale</h3>
      <p className="mt-1 text-xs text-[#6B7280]">
        Le lasă cursanții la finalul chestionarului de feedback, cu acordul de publicare. Pe site, la „Ce spun profesorii”,
        apar doar cele publicate, de la 3 în sus ({published.length === 1 ? "unul publicat" : `${published.length} publicate`} acum).
      </p>
      <div className="mt-3 flex flex-wrap gap-1.5">
        {FILTERS.map((f) => (
          <button key={f || "all"} type="button" onClick={() => setFilter(f)} aria-pressed={filter === f}
            className={`rounded-full border px-2.5 py-1 text-[11px] font-semibold ${filter === f
              ? "border-[#965EA7] bg-[#965EA7]/10 text-[#7A4986]" : "border-[#E5E7EB] text-[#373A45] hover:bg-[#F5F6F6]"}`}>
            {f ? STATUS_WORDS[f] : "Toate"} ({f === "" ? rows.length : rows.filter((t) => t.status === f).length})
          </button>
        ))}
      </div>
      {shown.length === 0 ? (
        <p className="mt-3 text-sm text-[#6B7280]">Niciun testimonial aici.</p>
      ) : (
        <ul className="mt-3 grid gap-3">
          {shown.map((t) => (
            <li key={t.id} className="rounded-xl border border-[#E5E7EB] p-4" data-testid={`testimonial-${t.id}`}>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="text-sm font-semibold text-[#373A45]">
                  {t.name}<span className="font-normal text-[#6B7280]">{[t.role, t.city].filter(Boolean).length ? ` · ${[t.role, t.city].filter(Boolean).join(", ")}` : ""}</span>
                </span>
                <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${STATUS_TONE[t.status]}`}>{STATUS_WORDS[t.status]}</span>
              </div>
              <p className="mt-1 text-[11px] text-[#6B7280]">{t.group_name}{t.trainer_name ? ` · ${t.trainer_name}` : ""} · {shortDay(t.created_at)}</p>
              {editing?.id === t.id ? (
                <div className="mt-2">
                  <textarea value={editing.text} onChange={(e) => setEditing({ ...editing, text: e.target.value })} rows={3} maxLength={420}
                    className="w-full rounded-lg border border-[#E5E7EB] px-3 py-2 text-sm text-[#373A45] outline-none focus:ring-2 focus:ring-[#965EA7]" />
                  <p className="mt-1 text-[11px] text-[#6B7280]">Poți tăia sau corecta greșeli de scriere, fără să adaugi cuvinte. Originalul rămâne aici.</p>
                  <div className="mt-2 flex gap-2">
                    <button type="button" disabled={busy} onClick={save}
                      className="rounded-full bg-[#965EA7] px-3 py-1 text-xs font-semibold text-white hover:bg-[#7A4986] disabled:opacity-50">Salvează</button>
                    <button type="button" disabled={busy} onClick={() => setEditing(null)}
                      className="rounded-full border border-[#E5E7EB] px-3 py-1 text-xs font-semibold text-[#373A45]">Renunță</button>
                  </div>
                </div>
              ) : (
                <p className="mt-2 whitespace-pre-line text-sm text-[#373A45]">{t.text}</p>
              )}
              {t.edited && editing?.id !== t.id && (
                <details className="mt-1 text-[11px] text-[#6B7280]"><summary className="cursor-pointer">Textul original</summary>{t.original_text}</details>
              )}
              {canMutate && t.status !== "retracted" && editing?.id !== t.id && (
                <div className="mt-3 flex flex-wrap gap-2">
                  {t.status !== "published" && (
                    <button type="button" disabled={busy} onClick={() => status(t, "published", "Testimonialul e publicat.")}
                      className="rounded-full bg-[#965EA7] px-3 py-1 text-xs font-semibold text-white hover:bg-[#7A4986] disabled:opacity-50">Publică</button>
                  )}
                  {t.status !== "unpublished" && (
                    <button type="button" disabled={busy} onClick={() => status(t, "unpublished", "Testimonialul nu se publică.")}
                      className="rounded-full border border-[#E5E7EB] px-3 py-1 text-xs font-semibold text-[#373A45] hover:bg-[#F5F6F6]">Nu publica</button>
                  )}
                  <button type="button" disabled={busy} onClick={() => setEditing({ id: t.id, text: t.text })}
                    className="rounded-full border border-[#E5E7EB] px-3 py-1 text-xs font-semibold text-[#373A45] hover:bg-[#F5F6F6]">Scurtează</button>
                  <button type="button" disabled={busy} onClick={() => retract(t)}
                    className="rounded-full border border-[#E63946]/30 px-3 py-1 text-xs font-semibold text-[#b42a35] hover:bg-[#E63946]/5">Retrage</button>
                  {t.status === "published" && filter === "published" && (
                    <>
                      <button type="button" disabled={busy} onClick={() => move(t, -1)} aria-label="Mai sus"
                        className="rounded-full border border-[#E5E7EB] p-1 text-[#373A45] hover:bg-[#F5F6F6]"><ArrowUp size={12} /></button>
                      <button type="button" disabled={busy} onClick={() => move(t, 1)} aria-label="Mai jos"
                        className="rounded-full border border-[#E5E7EB] p-1 text-[#373A45] hover:bg-[#F5F6F6]"><ArrowDown size={12} /></button>
                    </>
                  )}
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
      {filter !== "published" && published.length > 1 && (
        <p className="mt-3 text-[11px] text-[#6B7280]">Ordinea de pe site o schimbi din filtrul „Publicat”.</p>
      )}
    </div>
  );
}
